import React, { useState, useEffect, useRef } from 'react';
import { LogoutProps } from './props';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSignOutAlt, faCogs, faPenToSquare } from '@fortawesome/free-solid-svg-icons';
import { Client, ResponseType } from '@microsoft/microsoft-graph-client';
import { PublicClientApplication } from '@azure/msal-browser';
import { configuration } from '../../../constants/auth';
import '../../../css/fab.css';

interface LogoutBtnProps extends LogoutProps {
  initials: string;
  hasFirstMessageSent: boolean;
  userName: string;
  userRole: string;
  canAccessDes: boolean;
  environment: 'des' | 'prod'; // 👈 Nuevo prop para saber entorno
  onEnvironmentChange: (env: 'des' | 'prod') => void;
}

const msalInstance = new PublicClientApplication(configuration);

const initializeMsal = async () => {
  await msalInstance.initialize();
};

const LogoutBtn: React.FC<LogoutBtnProps> = ({
  userToken,
  logout,
  initials,
  hasFirstMessageSent,
  userName,
  userRole,
  canAccessDes,
  environment,
  onEnvironmentChange,
}) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [profilePhoto, setProfilePhoto] = useState<string | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  // 🧭 Log del entorno actual
  useEffect(() => {
    //console.log('🌍 Entorno actual (prop environment):', environment);
  }, [environment]);

  useEffect(() => {
    document.documentElement.setAttribute('data-theme', 'dark');
    localStorage.setItem('theme', 'dark');
  }, []);

  // Cerrar menú al hacer clic fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target as Node) &&
        !(event.target as HTMLElement).closest('.logout-btn')
      ) {
        setIsMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleButtonClick = () => setIsMenuOpen((prev) => !prev);

  // Obtener foto de perfil del usuario (MS Graph)
  useEffect(() => {
    if (!userToken) return;

    const getProfilePhoto = async () => {
      try {
        await initializeMsal();
        const account = msalInstance.getAllAccounts()[0];
        if (account) {
          const tokenResponse = await msalInstance.acquireTokenSilent({
            scopes: ['User.Read'],
            account,
          });

          const client = Client.init({
            authProvider: (done) => done(null, tokenResponse.accessToken),
          });

          const photoResponse = await client
            .api('/me/photo/$value')
            .responseType(ResponseType.BLOB)
            .get();

          if (photoResponse) {
            const url = URL.createObjectURL(photoResponse);
            setProfilePhoto(url);
          } else {
            throw new Error('Photo not found');
          }
        }
      } catch (error: any) {
        console.error('Error fetching profile photo:', error.message);
        setProfilePhoto(null);
      }
    };

    getProfilePhoto();
  }, [userToken]);

  const resetChat = () => {
    const path = decodeURIComponent(window.location.pathname).replace(/^\/+/, '').replace(/\/+$/, '');
    if (path) {
      window.location.replace('/');
    } else {
      window.location.reload();
    }
  };

  return (
    userToken && (
      <div className="logout-container">
        {hasFirstMessageSent && (
          <>
            <button className="nuevo-chat-btn" onClick={resetChat} aria-label="Nuevo Chat" title="Nuevo Chat">
              <FontAwesomeIcon icon={faPenToSquare} />
            </button>
            
          </>
        )}

        <button className="logout-btn" onClick={handleButtonClick}>
          {profilePhoto ? (
            <img src={profilePhoto} alt="Profile" className="profile-photo" />
          ) : (
            <div className="initials">{initials}</div>
          )}
        </button>

        {isMenuOpen && (
          <div className="logout-menu" ref={menuRef}>
            <div className="user-info">
              {profilePhoto ? (
                <img src={profilePhoto} alt="Profile" className="menu-profile-photo" />
              ) : (
                <div className="initials menu-initials">{initials}</div>
              )}
              <div className="user-details">
                <p className="user-name">{userName}</p>
                <p className="user-role">{userRole}</p>
              </div>
            </div>

            {canAccessDes && (
              <>
                <button onClick={() => window.open('/gestion-servicios', '_blank', 'noopener,noreferrer')}>
                  <FontAwesomeIcon icon={faCogs} /> Gestión de Servicios
                </button>
                <div className="env-switcher">
                  <label htmlFor="env-select" className="env-label">
                    Entorno:
                  </label>
                  <select
                    id="env-select"
                    className="env-select"
                    value={environment}
                    onChange={(e) => onEnvironmentChange(e.target.value as 'des' | 'prod')}
                  >
                    <option value="prod">Producción</option>
                    <option value="des">Desarrollo</option>
                  </select>
                </div>
              </>
            )}
            <button onClick={logout}>
              <FontAwesomeIcon icon={faSignOutAlt} /> Cerrar sesión
            </button>

          </div>
        )}
      </div>
    )
  );
};

export default LogoutBtn;
